import { getMockUID, getUID } from "../api/ajax";

/**
 * In-progress store-creation wizard. MUST be scoped to the effective seller
 * (impersonated mock user, else the signed-in admin). A single shared key
 * leaked Independent Bank's import URL into another seller's + New store.
 */

export const STORE_WIZARD_DRAFT_PREFIX = "riverr:store-wizard-draft:v1";
export const STORE_WIZARD_DRAFT_LEGACY_KEY = STORE_WIZARD_DRAFT_PREFIX;

function usableUid(value) {
  const uid = String(value || "").trim();
  if (!uid || uid === "undefined" || uid === "null") return "";
  return uid;
}

export function wizardOwnerUid() {
  return usableUid(getMockUID()) || usableUid(getUID());
}

export function wizardDraftKey(uid = wizardOwnerUid()) {
  return uid
    ? `${STORE_WIZARD_DRAFT_PREFIX}:${uid}`
    : STORE_WIZARD_DRAFT_LEGACY_KEY;
}

export function dropUnscopedStoreWizardDraft() {
  try {
    window.localStorage.removeItem(STORE_WIZARD_DRAFT_LEGACY_KEY);
  } catch (e) {
    /* private-mode / quota */
  }
}

function parseDraft(raw) {
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw);
    return draft && typeof draft === "object" ? draft : null;
  } catch (e) {
    return null;
  }
}

function isUsableDraft(draft, resumableSteps, owner) {
  if (!draft) return false;
  if (draft.step && resumableSteps && !resumableSteps.has(draft.step)) {
    return false;
  }
  if (draft.ownerUid && owner && usableUid(draft.ownerUid) !== owner) {
    return false;
  }
  return true;
}

export function loadWizardDraft(resumableSteps) {
  const owner = wizardOwnerUid();
  try {
    if (owner) {
      const scoped = parseDraft(window.localStorage.getItem(wizardDraftKey(owner)));
      if (isUsableDraft(scoped, resumableSteps, owner)) return scoped;
    }
    // Never restore the pre-scoped key while impersonating — that is how a
    // previous store's URL showed up on an empty seller account.
    if (usableUid(getMockUID())) return null;
    const legacy = parseDraft(
      window.localStorage.getItem(STORE_WIZARD_DRAFT_LEGACY_KEY),
    );
    if (!isUsableDraft(legacy, resumableSteps, owner)) return null;
    if (legacy.ownerUid && usableUid(legacy.ownerUid) !== owner) return null;
    return legacy;
  } catch (e) {
    return null;
  }
}

export function saveWizardDraft(draft) {
  const owner = wizardOwnerUid();
  try {
    const payload = {
      ...draft,
      ...(owner ? { ownerUid: owner } : {}),
    };
    window.localStorage.setItem(wizardDraftKey(owner), JSON.stringify(payload));
    if (owner) dropUnscopedStoreWizardDraft();
  } catch (e) {
    /* quota / private-mode — persistence is best-effort */
  }
}

export function clearWizardDraft() {
  const owner = wizardOwnerUid();
  try {
    if (owner) window.localStorage.removeItem(wizardDraftKey(owner));
    dropUnscopedStoreWizardDraft();
  } catch (e) {
    /* no-op */
  }
}
